import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Feather, ChevronLeft, Plus, FileText, BookMarked, ImagePlus, FileDown,
  Trash2, Settings2, PanelsTopLeft,
} from "lucide-react";

export default function Sidebar({
  project, chapters, activeChapterId, view,
  onSelectChapter, onAddChapter, onDeleteChapter,
  onSetView, onOpenCover, onOpenExport, onOpenSettings,
}) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const navItem = (icon, label, active, onClick, testid) => {
    const Icon = icon;
    return (
      <button
        onClick={onClick}
        data-testid={testid}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
          active ? "bg-gold/12 text-gold" : "text-slate-400 hover:text-slate-200 hover:bg-white/5"
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" /> {label}
      </button>
    );
  };

  return (
    <aside className="w-[270px] shrink-0 h-screen sticky top-0 bg-ink-800/70 border-r border-white/5 flex flex-col">
      <div className="px-4 py-4 border-b border-white/5">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-gold transition-colors mb-3" data-testid="studio-back-btn">
          <ChevronLeft className="w-3.5 h-3.5" /> {t("studio.back")}
        </button>
        <div className="flex items-center gap-2">
          <Feather className="w-4 h-4 text-gold shrink-0" />
          <h2 className="font-serif text-lg font-semibold text-slate-100 leading-tight line-clamp-2">{project?.title}</h2>
        </div>
        <div className="mt-3">
          <LanguageToggle />
        </div>
      </div>

      <div className="px-3 py-3 space-y-1">
        {navItem(PanelsTopLeft, t("studio.chapters"), view === "editor", () => onSetView("editor"), "nav-chapters")}
        {navItem(BookMarked, t("studio.bible"), view === "bible", () => { onSetView("bible"); }, "nav-bible")}
        {navItem(ImagePlus, t("studio.cover"), false, onOpenCover, "nav-cover")}
        {navItem(FileDown, t("studio.export"), false, onOpenExport, "nav-export")}
        {navItem(Settings2, t("studio.settings"), false, onOpenSettings, "nav-settings")}
      </div>

      <div className="px-4 pt-2 pb-1 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{t("studio.chapters")}</span>
        <button onClick={onAddChapter} className="text-gold hover:text-gold-light" data-testid="studio-add-chapter" title={t("studio.addChapter")}>
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <ScrollArea className="flex-1 px-3">
        <div className="space-y-1 pb-6">
          {chapters.map((c, i) => (
            <div
              key={c.id}
              className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                activeChapterId === c.id && view === "editor" ? "bg-gold/12 text-gold" : "text-slate-300 hover:bg-white/5"
              }`}
              onClick={() => onSelectChapter(c.id)}
              data-testid={`sidebar-${c.id}`}
            >
              <FileText className="w-3.5 h-3.5 shrink-0 opacity-70" />
              <span className="flex-1 text-sm truncate">{c.title || `${t("studio.chapter")} ${i + 1}`}</span>
              {chapters.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteChapter(c.id); }}
                  className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
                  data-testid={`delete-chapter-${c.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </aside>
  );
}
